import { toWhitespaceCss } from '@/components/tiptap-editor/tiptap-extension/zycode-inline-extension/whitespace-utils';
import { stripListItemParagraph } from '@/lib/tiptap-dom-html-utils';
import type { MarkProps, NodeProps } from '@/lib/tiptap-static-renderer/json/renderer';
import { serializeChildrenToHTMLString } from '@/lib/tiptap-static-renderer/pm/html-string/html-string';

function escapeXml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

export const zyTextHTMLStaticRenderer = ({ node }: NodeProps) => {
    return escapeXml(node.text ?? '');
};

export const inlineMathHTMLStaticRenderer = ({ node }: NodeProps) => {
    if (node.attrs.mathml) {
        return node.attrs.mathml;
    }
    return `\\(${node.attrs.latex}\\)`;
};

export const blockMathHTMLStaticRenderer = ({ node }: NodeProps) => {
    if (node.attrs.mathml) {
        return node.attrs.mathml;
    }
    return `\\[${node.attrs.latex}\\]`;
};

export const zyDefnHTMLStaticRenderer = ({ children }: NodeProps) => {
    if (!children || children.length === 0) {
        return '';
    }
    if (typeof children === 'string') {
        return children;
    }
    const content = children.join('');
    return `<span data-definition="true">${content}</span>`;
};

export const zyDefnBlockHTMLStaticRenderer = ({ children }: NodeProps) => {
    if (!children || children.length === 0) {
        return '';
    }
    if (typeof children === 'string') {
        return children;
    }

    const content = children.join('');
    return `<span data-definition="true">${content}</span>`;
};

export const zyDefnTermHTMLStaticRenderer = ({ children }: NodeProps) => {
    if (!children || children.length === 0) {
        return '';
    }
    const attrs = 'class="term zydefn-term" data-term="true" data-zydefn-term="true" obeycase="true"';
    if (typeof children === 'string') {
        return `<span ${attrs}>${children}</span>`;
    }
    return `<span ${attrs}>${children.join('')}</span>`;
};

export const zyTermHTMLStaticRenderer = ({ children }: NodeProps) => {
    if (!children || children.length === 0) {
        return '';
    }
    if (typeof children === 'string') {
        return `<span class="term" data-term="true" obeycase="true">${children}</span>`;
    }
    return `<span class="term" data-term="true" obeycase="true">${children.join('')}</span>`;
};

export function zyCodeInlineStaticRenderer({ node, mark, children }: MarkProps): string {
    if (node.type.name === 'hardBreak') {
        return serializeChildrenToHTMLString(children);
    }
    const preserve = !!mark.attrs.preserveWhitespace;
    const allow = !!mark.attrs.allowWrap;
    const css = toWhitespaceCss(preserve, allow);
    const style = css ? ` style="white-space: ${css};"` : '';
    return `<code${style}>${serializeChildrenToHTMLString(children)}</code>`;
}

export const zyListItemHTMLStaticRenderer = ({ children }: NodeProps) => {
    const content = serializeChildrenToHTMLString(children);
    return stripListItemParagraph(`<li>${content}</li>`);
};
